import React, { useState, useEffect } from 'react';
import { trackEvent } from '../services/analyticsService';
import { demoService } from '../services/demoService';

interface DemoBannerProps {
  onSignUp: () => void;
  onSignIn: () => void;
}

const DemoBanner: React.FC<DemoBannerProps> = ({ onSignUp, onSignIn }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('6h 0m');

  useEffect(() => {
    // Check if we're in demo mode
    const isDemoMode = sessionStorage.getItem('is_demo_mode') === 'true';
    if (isDemoMode) {
      setIsVisible(true);
      
      // Get demo user data and calculate time remaining
      const demoUserStr = sessionStorage.getItem('demo_user');
      if (demoUserStr) {
        const demoUser = JSON.parse(demoUserStr);
        const progress = demoService.getDemoProgress(demoUser);
        setTimeRemaining(progress.timeRemaining);
      }
      
      // Update time remaining every minute
      const interval = setInterval(() => {
        const demoUserStr = sessionStorage.getItem('demo_user');
        if (demoUserStr) {
          const demoUser = JSON.parse(demoUserStr);
          const progress = demoService.getDemoProgress(demoUser);
          setTimeRemaining(progress.timeRemaining);
          
          // If time is up, show warning
          if (progress.timeRemaining === '0h 0m') {
            setTimeRemaining('EXPIRED');
          }
        }
      }, 60000); // Update every minute
      
      return () => clearInterval(interval);
    }
  }, []);

  const handleSignUp = () => {
    trackEvent('demo_banner_signup_clicked', {
      banner_location: 'top_banner',
      demo_session: true,
    });
    onSignUp();
  };

  const handleSignIn = () => {
    trackEvent('demo_banner_signin_clicked', {
      banner_location: 'top_banner',
      demo_session: true,
    });
    onSignIn();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 backdrop-blur-sm border-b border-yellow-500/30" style={{ 
      top: 'env(safe-area-inset-top, 0px)',
      paddingTop: 'max(env(safe-area-inset-top, 0px), 44px)'
    }}>
      <div className="px-3 sm:px-4 py-2 sm:py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-yellow-400 text-sm sm:text-lg">🎮</span>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-yellow-400 font-orbitron font-bold text-xs sm:text-sm uppercase tracking-wider">
                Demo Mode
              </h3>
              <p className="text-yellow-300/80 text-[10px] sm:text-xs font-rajdhani">
                Time: <span className={`font-bold ${timeRemaining === 'EXPIRED' ? 'text-red-400' : timeRemaining.includes('0h') ? 'text-orange-400' : 'text-yellow-400'}`}>
                  {timeRemaining}
                </span>
              </p>
              <p className="text-yellow-300/60 text-[9px] sm:text-xs font-rajdhani hidden sm:block">
                Progress will be lost. Sign up to start your real journey!
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <button
              onClick={handleSignIn}
              className="px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-600/20 border border-blue-500/50 text-blue-400 hover:bg-blue-600/30 hover:border-blue-400 text-[10px] sm:text-xs font-orbitron font-bold uppercase tracking-wider rounded transition-all"
            >
              <span className="hidden sm:inline">Sign In</span>
              <span className="sm:hidden">Login</span>
            </button>
            <button
              onClick={handleSignUp}
              className="px-2 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-yellow-600 to-orange-600 text-white hover:from-yellow-500 hover:to-orange-500 text-[10px] sm:text-xs font-orbitron font-bold uppercase tracking-wider rounded transition-all shadow-lg"
            >
              <span className="hidden sm:inline">Sign Up</span>
              <span className="sm:hidden">Join</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoBanner;
